<template>
  <div class="rte">
    <!-- Primary toolbar -->
    <div v-if="editor" class="rte-toolbar d-flex flex-wrap gap-1 mb-2 align-items-center">
      <select class="form-select form-select-sm w-auto" @change="onHeadingChange($event)" :value="currentHeading">
        <option value="paragraph">Paragraph</option>
        <option value="h1">Heading 1</option>
        <option value="h2">Heading 2</option>
        <option value="h3">Heading 3</option>
        <option value="h4">Heading 4</option>
      </select>
      <div class="btn-group btn-group-sm" role="group">
        <button type="button" :class="btnToggle('bold')" @click="cmd('toggleBold')" title="Bold"><strong>B</strong></button>
        <button type="button" :class="btnToggle('italic')" @click="cmd('toggleItalic')" title="Italic"><em>I</em></button>
        <button type="button" :class="btnToggle('underline')" @click="cmd('toggleUnderline')" title="Underline"><u>U</u></button>
        <button type="button" :class="btnToggle('strike')" @click="cmd('toggleStrike')" title="Strikethrough">S</button>
        <button type="button" :class="btnToggle('highlight')" @click="cmd('toggleHighlight')" title="Highlight">Hl</button>
      </div>
      <div class="btn-group btn-group-sm" role="group">
        <button type="button" :class="btnToggle('bulletList')" @click="cmd('toggleBulletList')" title="Bullet List">• List</button>
        <button type="button" :class="btnToggle('orderedList')" @click="cmd('toggleOrderedList')" title="Numbered List">1. List</button>
        <button type="button" :class="btnToggle('blockquote')" @click="cmd('toggleBlockquote')" title="Quote">“”</button>
        <button type="button" :class="btnToggle('codeBlock')" @click="cmd('toggleCodeBlock')" title="Code Block">{ }</button>
        <button type="button" :class="btnToggle('code')" @click="cmd('toggleCode')" title="Inline Code">&lt;/&gt;</button>
      </div>
      <div class="btn-group btn-group-sm" role="group">
        <button type="button" class="btn btn-sm btn-outline-secondary" @click="addLink" title="Add / Edit Link">Link</button>
        <button type="button" class="btn btn-sm btn-outline-secondary" @click="removeLink" title="Remove Link">Unlink</button>
        <button type="button" class="btn btn-sm btn-outline-secondary" @click="triggerImageUpload" title="Upload Image" :disabled="isUploading">
          {{ isUploading ? '⏳ Uploading...' : 'Image' }}
        </button>
        <button type="button" class="btn btn-sm btn-outline-secondary" @click="addYouTube" title="Embed YouTube Video">YouTube</button>
        <button type="button" class="btn btn-sm btn-outline-secondary" @click="addVimeo" title="Embed Vimeo Video">Vimeo</button>
      </div>
      <div class="btn-group btn-group-sm" role="group">
        <button type="button" :class="btnToggleAlign('left')" @click="setAlign('left')" title="Align Left">⯇</button>
        <button type="button" :class="btnToggleAlign('center')" @click="setAlign('center')" title="Align Center">≡</button>
        <button type="button" :class="btnToggleAlign('right')" @click="setAlign('right')" title="Align Right">⯈</button>
      </div>
      <div class="btn-group btn-group-sm" role="group">
        <button type="button" class="btn btn-sm btn-outline-secondary" @click="cmd('setHorizontalRule')" title="Horizontal Rule">HR</button>
        <button type="button" class="btn btn-sm btn-outline-secondary" @click="clearFormatting" title="Clear Formatting">Clear</button>
      </div>
      <div class="btn-group btn-group-sm" role="group">
        <button type="button" class="btn btn-sm btn-outline-secondary" @click="cmd('undo')" title="Undo">↺</button>
        <button type="button" class="btn btn-sm btn-outline-secondary" @click="cmd('redo')" title="Redo">↻</button>
      </div>
    </div>

    <!-- Floating menu for quick block inserts (like TipTap demo) -->
    <FloatingMenu v-if="editor" :editor="editor" class="rte-floating shadow-sm">
      <button class="btn btn-sm btn-outline-secondary" @click="cmd('toggleBulletList')">• List</button>
      <button class="btn btn-sm btn-outline-secondary" @click="cmd('toggleOrderedList')">1. List</button>
      <button class="btn btn-sm btn-outline-secondary" @click="cmd('toggleBlockquote')">“”</button>
      <button class="btn btn-sm btn-outline-secondary" @click="cmd('toggleCodeBlock')">{ }</button>
    </FloatingMenu>

    <!-- Bubble menu for inline formatting -->
    <BubbleMenu v-if="editor" :editor="editor" class="rte-bubble shadow-sm">
      <button class="btn btn-sm" :class="btnToggle('bold')" @click="cmd('toggleBold')">B</button>
      <button class="btn btn-sm" :class="btnToggle('italic')" @click="cmd('toggleItalic')">I</button>
      <button class="btn btn-sm" :class="btnToggle('underline')" @click="cmd('toggleUnderline')">U</button>
      <button class="btn btn-sm" :class="btnToggle('strike')" @click="cmd('toggleStrike')">S</button>
      <button class="btn btn-sm" :class="btnToggle('code')" @click="cmd('toggleCode')">&lt;/&gt;</button>
      <button class="btn btn-sm" :class="btnToggle('highlight')" @click="cmd('toggleHighlight')">Hl</button>
      <button class="btn btn-sm btn-outline-secondary" @click="addLink">Link</button>
    </BubbleMenu>

    <div class="rte-editor form-control" :class="{ 'is-invalid': error, 'drag-over': isDragOver }" 
         @dragover.prevent="handleDragOver"
         @dragleave.prevent="handleDragLeave" 
         @drop.prevent="handleDrop">
      <EditorContent v-if="editor" :editor="editor" />
      <div v-if="isUploading" class="upload-overlay">
        <div class="upload-spinner">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Uploading...</span>
          </div>
          <div class="mt-2">Uploading image...</div>
        </div>
      </div>
    </div>
    <!-- Hidden file input for image upload -->
    <input 
      ref="fileInput" 
      type="file" 
      accept="image/*" 
      style="display: none" 
      @change="handleFileUpload"
    />
    <div v-if="error" class="invalid-feedback d-block">{{ error }}</div>
  </div>
</template>

<script setup lang="ts">
import { EditorContent, useEditor, BubbleMenu, FloatingMenu } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Image from '@tiptap/extension-image';
import Youtube from '@tiptap/extension-youtube';
import Iframe from '@/components/common/extensions/Iframe';
import TextAlign from '@tiptap/extension-text-align';
import Highlight from '@tiptap/extension-highlight';
import Placeholder from '@tiptap/extension-placeholder';
import { axiosFactory } from '@/tools/axios-factory';
import { watch, computed, ref } from 'vue';

interface Props {
  modelValue?: string | null;
  error?: string;
  imagePrompt?: string;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  imagePrompt: 'Enter image URL'
});
const emit = defineEmits<{ (e: 'update:modelValue', v: string): void }>();

// File input reference
const fileInput = ref<HTMLInputElement | null>(null);

// Upload state
const isDragOver = ref(false);
const isUploading = ref(false);

// Upload function
const uploadImage = async (file: File): Promise<string> => {
  const factory = new axiosFactory();
  const formData = new FormData();
  formData.append('Upload', file);

  try {
    const response = await factory
      .withoutToken()
      .post('/api/FileUpload/Upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

    if (response.data?.url) {
      return response.data.url;
    } else {
      throw new Error(response.data?.message || 'Upload failed');
    }
  } catch (error) {
    console.error('Upload error:', error);
    throw error;
  }
};

const editor = useEditor({
  content: props.modelValue || '',
  extensions: [
    StarterKit.configure({
      heading: { levels: [1,2,3,4] }
    }),
    Underline,
    Link.configure({
      openOnClick: true,
      autolink: true,
      HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' }
    }),
    Image.configure({ HTMLAttributes: { class: 'img-fluid' } }),
    Highlight,
    TextAlign.configure({ types: ['heading', 'paragraph'] }),
  (Youtube as any).configure({
      controls: true,
      nocookie: true,
      width: 640,
      height: 360,
      allowFullscreen: true,
      modestBranding: true,
      HTMLAttributes: { class: 'rte-video youtube-embed ratio ratio-16x9' }
    }),
    Iframe,
    Placeholder.configure({
      placeholder: 'Start typing …'
    })
  ],
  editorProps: {
    attributes: { class: 'tiptap-content' }
  },
  onUpdate: ({ editor }) => emit('update:modelValue', editor.getHTML())
});

watch(
  () => props.modelValue,
  (val) => {
    if (!editor.value || val == null) return;
    const current = (editor.value as any).getHTML();
    if (val !== current) {
      const { from, to } = editor.value.state.selection;
      (editor.value as any).commands.setContent(val, false);
      (editor.value as any).commands.setTextSelection({ from, to });
    }
  }
);

const btnClass = (active: boolean) => (active ? 'btn-primary active' : 'btn-outline-secondary');

function core() { return editor.value as any; }
function isActive(name: string, attrs: any = {}) {
  return !!core()?.isActive?.(name, attrs);
}
function isActiveAlign(alignment: string) {
  return !!core()?.isActive?.({ textAlign: alignment });
}
function cmd(command: string) {
  const ed: any = core();
  if (!ed) return;
  if (typeof ed.commands?.[command] === 'function') ed.chain().focus()[command]().run();
  else if (typeof ed[command] === 'function') ed.chain().focus()[command]().run();
}
function setAlign(alignment: string) {
  (editor.value as any)?.chain().focus().setTextAlign(alignment).run();
}
function btnToggle(name: string) {
  return btnClass(isActive(name));
}
function btnToggleAlign(alignment: string) {
  return btnClass(isActiveAlign(alignment));
}

const addLink = () => {
  if (!editor.value) return;
  const prev = core().getAttributes?.('link')?.href;
  const url = window.prompt('Enter URL', prev || 'https://');
  if (url === null) return;
  if (url.trim() === '') {
    core().chain().focus().extendMarkRange('link').unsetLink().run();
    return;
  }
  core().chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run();
};

const removeLink = () => core()?.chain()?.focus().unsetLink().run();

// Helper to validate image URLs
function isValidImageUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    // Only allow http(s) protocols
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return false;
    // Optionally, restrict to allowed domains (example: only allow images from specific domains)
    // const allowedDomains = ['images.example.com', 'cdn.example.com'];
    // if (!allowedDomains.includes(parsed.hostname)) return false;
    return true;
  } catch {
    return false;
  }
}

const addImage = () => {
  if (!editor.value) return;
  const url = window.prompt(props.imagePrompt, 'https://');
  if (!url) return;
  if (!isValidImageUrl(url.trim())) {
    alert('Invalid image URL. Please enter a valid http(s) URL.');
    return;
  }
  core()?.chain()?.focus().setImage({ src: url.trim() }).run();
};

const addYouTube = () => {
  if (!editor.value) return;
  const input = window.prompt('Enter YouTube URL or ID', '');
  if (!input) return;
  const idMatch = input.match(/(?:v=|youtu\.be\/|embed\/)([\w-]{11})/);
  const id = idMatch ? idMatch[1] : (input.length === 11 ? input : null);
  if (!id) {
    alert('Invalid YouTube URL/ID');
    return;
  }
  const src = `https://www.youtube-nocookie.com/embed/${id}`;
  core()?.chain()?.focus().setYoutubeVideo({ src, width: 640, height: 360 }).run();
};

const addVimeo = () => {
  if (!editor.value) return;
  const input = window.prompt('Enter Vimeo URL or ID', '');
  if (!input) return;
  const idMatch = input.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  const id = idMatch ? idMatch[1] : (/^\d+$/.test(input) ? input : null);
  if (!id) {
    alert('Invalid Vimeo URL/ID');
    return;
  }
  const src = `https://player.vimeo.com/video/${id}`;
  core()?.chain()?.focus().insertIframe({ src, allowfullscreen: 'true', frameborder: 0, width: 640, height: 360 }).run();
};

// useEditor handles lifecycle cleanup

const error = computed(() => props.error);

const currentHeading = computed(() => {
  if (!editor.value) return 'paragraph';
  for (const level of [1,2,3,4]) {
  if (core()?.isActive?.('heading', { level })) return `h${level}`;
  }
  return 'paragraph';
});

function onHeadingChange(e: Event) {
  if (!editor.value) return;
  const value = (e.target as HTMLSelectElement).value;
  const ed = core();
  if (!ed) return;
  if (value === 'paragraph') {
    ed.chain().focus().setParagraph().run();
  } else {
    const level = Number(value.substring(1));
    ed.chain().focus().toggleHeading({ level }).run();
  }
}

function clearFormatting() {
  core()?.chain()?.focus().clearNodes().unsetAllMarks().run();
}

// File upload functions
const triggerImageUpload = () => {
  fileInput.value?.click();
};

const handleFileUpload = async (event: Event) => {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  
  if (!file) return;
  
  await processImageUpload(file);
  
  // Clear the file input
  target.value = '';
};

const processImageUpload = async (file: File) => {
  // Validate file type
  if (!file.type.startsWith('image/')) {
    alert('Please select an image file.');
    return;
  }

  // Validate file size (e.g., max 5MB)
  const maxSize = 5 * 1024 * 1024; // 5MB in bytes
  if (file.size > maxSize) {
    alert('File size must be less than 5MB.');
    return;
  }

  try {
    isUploading.value = true;
    
    // Upload the file
    const imageUrl = await uploadImage(file);
    
    // Insert the uploaded image
    core()?.chain()?.focus().setImage({ src: imageUrl, alt: file.name }).run();
    
  } catch (error) {
    console.error('Upload failed:', error);
  } finally {
    isUploading.value = false;
  }
};

// Drag and drop functions
const handleDragOver = (event: DragEvent) => {
  event.preventDefault();
  isDragOver.value = true;
};

const handleDragLeave = (event: DragEvent) => {
  event.preventDefault();
  isDragOver.value = false;
};

const handleDrop = async (event: DragEvent) => {
  event.preventDefault();
  isDragOver.value = false;
  
  const files = event.dataTransfer?.files;
  if (!files || files.length === 0) return;
  
  const file = files[0];
  await processImageUpload(file);
};

</script>

<style>
.tiptap-content {
    padding: 12px;
    min-height: 300px;
}
.tiptap-content p {
  margin: 0 0 0.75rem;
}
.tiptap-content ul,
.tiptap-content ol {
  padding-left: 1.25rem;
  margin: 0 0 0.75rem;
}
.btn-group .btn {
  margin: 0 0.25rem 0.25rem 0;
}
/* Ensure caret (text cursor) is clearly visible */
.rte-editor .tiptap-content {
  caret-color: #3b82f6; /* bright blue caret */
  color: #212529;       /* ensure readable text color */
}
.rte-editor .tiptap-content ::selection {
  background: #3b82f6;
  color: #fff;
}
/* Floating & bubble menus */
.rte-bubble, .rte-floating {
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: .375rem;
  padding: .25rem .5rem;
  display: flex;
  gap: .25rem;
}
.rte-bubble .btn, .rte-floating .btn { padding: .125rem .5rem; }
.rte-floating { transform: translateY(-4px); }
.rte-toolbar { background: #f8f9fa; border: 1px solid #dee2e6; border-radius: .375rem; padding: .25rem .5rem; }

/* Drag and drop styles */
.rte-editor {
  position: relative;
  transition: border-color 0.2s ease-in-out;
}

.rte-editor.drag-over {
  border-color: #007bff !important;
  border-width: 2px !important;
  background-color: rgba(0, 123, 255, 0.05);
}

/* Upload overlay */
.upload-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 0.375rem;
}

.upload-spinner {
  text-align: center;
  color: #007bff;
}

blockquote {
  margin: 1.5em 10px;
  padding: 1em 20px;
  border-left: 5px solid #ccc;
  background: #f9f9f9;
  font-style: italic;
  color: #555;
}

blockquote p {
  margin: 0; /* remove extra space around paragraph */
  font-size: 1.1em;
  line-height: 1.6;
}
</style>